import template, { mainSlide, navArrow, navSlide, sliderModal } from './templates/pdp-slider';

const URL_IMAGE_NOT_AVAILABLE = '/images-2/image-not-available.svg';

const parsedTemplate = $.parseHTML(template);
const parsedModal = $.parseHTML(sliderModal);

export default class PdpSlider {
    /**
     * @typedef {{ url: string, thumbnail: string?, alt: string }} Slide
     * 
     * @param {Object<>} $el // JQuery element instance 
     * @param {Object<string,string>} labels // object of label strings
     * @param {boolean} rx // prescription icon should display
     * @param {Array<Slide>} slides // array of image objects
     * @param {boolean} useTempalte // render template markup
     */
    constructor({ $el, labels, rx, slides, useTemplate }) {
        if(useTemplate) {
            $el.replaceWith(parsedTemplate);
        }
        
        this.$el = useTemplate ? $(parsedTemplate) : $el;

        // main slider labels
        this.$favoriteLabel = this.$el.find('[data-label="favorite"]');
        this.$loaderLabel = this.$el.find('[data-label="loader"]');
        this.$zoomLabel = this.$el.find('[data-label="zoom"]');

        // main slider regions
        this.$main = this.$el.find('[data-region="main"]');
        this.$nav = this.$el.find('[data-region="nav"]');
        this.$loader = this.$el.find('[data-region="loader"]');
        this.$rx = this.$el.find('[data-region="rx"]');

        this.$modal = $(parsedModal);

        // modal labels
        this.$modalLoaderLabel = this.$modal.find('[data-label="loader"]');

        // modal regions
        this.$modalHeading = this.$modal.find('[data-region="modalHeading"]');
        this.$modalImage = this.$modal.find('[data-region="modalImage"]');
        this.$modalLoader = this.$modal.find('[data-region="modalLoader"]');

        // main controls (null until they are rendered)
        this.$navControl = null;
        this.$zoomControl = null;

        this.rx = !!rx;
        this.slides = slides || [ ...this.$main.get(0)?.children ] || [];
        this.labels = labels || null;

        // need to track this id since bootstrap modal method breaks without
        this.modalId = this.$modal.attr('id');

        if(useTemplate) {
            this.renderSlides();
        }

        this.init();
    }

    /**
     * remove slick instance from all sliders
     * - can be used to update images and reset the slider while avoiding duplicate slick initializations
     */
    destroy() {
        this.$main.slick('unslick');
        this.$nav.slick('unslick');

        this.$main.empty();
        this.$nav.empty();
    }

    init() {
        // add modal element to document
        $('body').append(this.$modal);

        this.renderLabels();
        // setup the rest of the slider
        this.setup();

        // setup static listeners
        this.setupListeners();

        if(this.rx) this.$rx.show();
        else this.$rx.hide();
    }

    onNavClick(evt) {
        const $target = $(evt.currentTarget);
        const index = $target.closest('[data-slick-index]').data('slick-index');
        this.$main.slick('slickGoTo', index);
    }

    onZoomClick() {
        const index = this.$main.slick('slickCurrentSlide');
        const currentSlide = this.slides[index];

        this.renderModalInfo(currentSlide);

        $(`#${this.modalId}`).modal();
    }

    renderLabels() {
        if(this.labels) {
            this.$favoriteLabel.html(this.labels.FavoriteSrText);
            this.$loaderLabel.html(this.labels.LoaderSrText);
            this.$zoomLabel.html(this.labels.ZoomSrText);
            this.$modalLoaderLabel.html(this.labels.LoaderSrText);
        }
    }

    renderModalInfo(currentSlide) {
        const src = currentSlide.url || currentSlide.thumbnail;

        // lazy load image while showing loading spinner
        this.$modalLoader.show();
        this.$modalImage.hide();
        const img = new Image();
        img.onload = () => {
            this.$modalLoader.hide();
            this.$modalImage.show();
        };
        img.src = src;

        this.$modalHeading.html(currentSlide.alt);
        this.$modalImage.attr({
            alt: currentSlide.alt,
            src
        });

        this.$modal.one('hidden.bs.modal', () => this.$zoomControl.focus());
    }

    /**
     * render slides from markup templates
     */
    renderSlides() {
        let mainSlides = '';
        let navSlides = '';

        this.slides.forEach((slide, index) => {
            mainSlides += mainSlide(slide);
            navSlides += navSlide({
                ...slide,
                isButton: this.slides.length > 1,
                srText: `Go to slide ${index + 1}`
            });
        });

        this.$main.html($.parseHTML(mainSlides));
        this.$nav.html($.parseHTML(navSlides));
    }

    /**
     * initialize slick and toggle thumbnail nav as needed
     */
    setup() {
        this.$main.slick({
            arrows: false,
            asNavFor: this.$nav.get(),
            rows: 0
        });
        this.$nav.slick({
            asNavFor: this.$main.get(),
            draggable: false,
            infinite: true,
            nextArrow: navArrow(),
            prevArrow: null,
            slidesToShow: this.slides.length < 3 ? this.slides.length : 3,
            vertical: true,
            responsive: [
                {
                    breakpoint: 992,
                    settings: {
                        slidesToShow: 3,
                        vertical: false
                    }
                }
            ]
        });

        this.updateSlickTrack();
        this.$nav.on('breakpoint', () =>  this.setupNavListeners());
        this.setupNavListeners();
    }

    updateSlickTrack() {
        if (this.slides.length <= 3) {
            let slickTrack = this.$el.find('.slick-track')[0];
            $(slickTrack).addClass('modify');
        }
    }

    setupListeners() {
        this.$zoomControl = this.$el.find('[data-control="zoom"]');
        this.$zoomControl.on('click', this.onZoomClick.bind(this));
    }

    startLoading () {
        // prevent jumping
        this.$main.height(this.$main.height());
        this.$nav.height(this.$nav.height());
        this.$loader.addClass('-loading');
    }

    stopLoading () {
        this.$main.removeAttr('style');
        this.$nav.removeAttr('style');
        this.$loader.removeClass('-loading');
    }

    setupNavListeners() {
        this.$navControl = this.$el.find('[data-control="nav"]');
        this.$navControl.on('click', this.onNavClick.bind(this));
    }

    /**
     * update slides and reinitialize slick
     * @param {boolean} rx // prescription icon should display
     * @param {Array<Slide>} slides // array of slide objects
     */
    async updateSlides(rx, slides) {
        if(!slides || slides.length < 1) {
            slides = [{
                url: URL_IMAGE_NOT_AVAILABLE,
                thumbnail: URL_IMAGE_NOT_AVAILABLE,
                alt: 'Image not available'
            }];
        }

        this.rx = !!rx;
        if(this.rx) this.$rx.fadeIn('fast')
        else this.$rx.fadeOut('fast')

        this.slides = slides;

        this.startLoading();
        this.destroy();

        await this.loadSlides();

        this.renderSlides();
        this.setup();

        // make hidden until slick slider overrides inline style
        this.$main.css('visibility', 'hidden');
        this.$nav.css('visibility', 'hidden');

        setTimeout(() => {
            // set position after 200 ms because of incorrect image calculations
            this.$nav.slick('setPosition');
            this.stopLoading();
        }, 200);
    }

    /**
     * load slides before trying to setup slick
     * - prevents slick slider from miscalculating heights/widths when initialized
     * @returns {Promise}
     */
    async loadSlides() {
        return new Promise(resolve => {
            // array matching slides array indicating which images have loaded
            const loadedSlides = this.slides.map(() => false);

            // img.onload event handler
            const slideLoaded = index => {
                loadedSlides[index] = true;

                // if all items in the loadedSlides array is true, then all images are finished
                if(loadedSlides.every(isLoaded => isLoaded)) {
                    resolve()
                }
            };

            this.slides.forEach((slide, index) => {
                const img = new Image();
                img.onload = () => slideLoaded(index);
                img.src = slide.thumbnail;
            });
        });
    }
}

$(() => {
    $('.js-pdp-slider').each(function() {
        const $el = $(this);
        new PdpSlider({
            $el
        });
    });
})
